import type { ChatRoomMessageDto } from '@digital-twins/shared-types'

/**
 * Copy for a send whose HTTP call failed and whose clientRequestId is absent
 * from canonical history. Cleared when that id shows up later.
 */
export const UNCONFIRMED_DELIVERY_ERROR =
  'Delivery could not be confirmed. Check the conversation before retrying.'

function metadataRecord(message: ChatRoomMessageDto): Record<string, unknown> | null {
  if (!message.metadata || typeof message.metadata !== 'object') return null
  return message.metadata as Record<string, unknown>
}

/** User-message clientRequestId, when the server persisted one. */
export function clientRequestIdOf(message: ChatRoomMessageDto): string | null {
  if (message.senderType !== 'user') return null
  const id = metadataRecord(message)?.clientRequestId
  return typeof id === 'string' && id.length > 0 ? id : null
}

/**
 * Request id of a local optimistic copy, under its own key so the recovery checks
 * that read `clientRequestId` never take an unsent copy for a delivered message.
 */
export function pendingClientRequestIdOf(message: ChatRoomMessageDto): string | null {
  if (message.senderType !== 'user') return null
  const id = metadataRecord(message)?.pendingClientRequestId
  return typeof id === 'string' && id.length > 0 ? id : null
}

/** Request ids already present on canonical user messages. */
export function acceptedRequestIds(messages: Array<ChatRoomMessageDto>): Set<string> {
  return new Set(messages.flatMap((message) => {
    const id = clientRequestIdOf(message)
    return id ? [id] : []
  }))
}

/**
 * A twin reply belongs to a queued send when its responseTurnId matches the
 * user message that carries that clientRequestId.
 */
export function requestConfirmedByTwinReply(
  messages: Array<ChatRoomMessageDto>,
  clientRequestId: string,
  twinMessage: ChatRoomMessageDto,
): boolean {
  if (twinMessage.senderType !== 'twin') return false
  const responseTurnId = metadataRecord(twinMessage)?.responseTurnId
  if (typeof responseTurnId !== 'string' || responseTurnId.length === 0) return false
  return messages.some((message) => {
    if (clientRequestIdOf(message) !== clientRequestId) return false
    return metadataRecord(message)?.turnId === responseTurnId
  })
}
